import {
    AfterViewInit,
    Component,
    ContentChild,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef, ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import {ContentDirective} from '../../../../shared/directives/content.directive';
import {Sort, UrlParameter} from '../../../../shared/utils/url.builder';
import {PaginatedEntityCollectionReadOnlyService} from '../../service/paginatedEntityCollectionReadOnlyService';
import {HttpModelConverter} from '../../converter/httpModel.converter';
import {Page} from '../../../../shared/model/core/page.model';
import {PaginationCacheService} from '../../../common/cache/api/paginationCache.service';
import {Pageable} from '../../../../shared/model/core/pageable.model';
import {AbstractFindManyPaginatedService} from '../../service/v2/service/findmany/abstractFindManyPaginated.service';

@Component({
    selector: 'app-paginated-list-data',
    templateUrl: './paginated-list-data.component.html',
})
export class PaginatedListDataComponent<T> implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @ContentChild(ContentDirective, {static: true, read: TemplateRef}) template: TemplateRef<any>;

    @Input() cacheName: string;
    @Input() pageSize = 10;
    @Input() noResultLabel = 'Aucun résultat';
    @Input() parameters: UrlParameter[];
    @Input() sort: Sort;
    @Input() loadPageOnInit = true;
    @Input() withPagination = true;

    @Input() paginatedService: PaginatedEntityCollectionReadOnlyService<T, HttpModelConverter<T>>
        | AbstractFindManyPaginatedService<any, T>;

    error = false;
    loading = false;
    page: Page<T>;

    subscriptions: Subscription[] = [];

    constructor(private paginationCacheService: PaginationCacheService) {
    }

    get pageSizeSafetly(): number {
        return this.pageSize ? this.pageSize : 20;
    }

    ngOnInit() {
        this.subscriptions.push(this.paginatedService.loading$.subscribe(value => {
            this.loading = value;
        }));
        if (this.paginatedService instanceof PaginatedEntityCollectionReadOnlyService) {
            this.subscriptions.push(this.paginatedService.queryManyError$.subscribe(value => this.error = !!value));
        } else {
            this.subscriptions.push(this.paginatedService.error$.subscribe(value => this.error = !!value));
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.paginatedService instanceof PaginatedEntityCollectionReadOnlyService) {
            if (changes['sort']) {
                this.paginatedService.sort = this.sort;
            }
            if (changes['pageSize']) {
                this.paginatedService.pageSize = this.pageSize;
            }
        }
        if (changes['parameters']) {
            // console.log(['parameter changed', this.parameters, changes['parameters']]);
        }

        if ((changes['sort'] && !changes['sort'].isFirstChange()) ||
            (changes['parameters'] && !changes['parameters'].isFirstChange()) ||
            (changes['pageSize'] && !changes['pageSize'].isFirstChange())) {
            this.refresh();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    next(page: Page<T>) {
        this.getPage(page.number + 1);
    }

    previous(page: Page<T>) {
        this.getPage(page.number - 1);
    }

    ngAfterViewInit(): void {
        // console.log(['list', this.paginatedService]);
        this.subscriptions.push(this.paginatedService.page$.subscribe(value => {
            this.page = value;
        }));


        if (this.loadPageOnInit) {
            this.getPage(0);
        }
    }

    refresh() {
        this.getPage(this.page?.number ? this.page.number : 0);
    }

    getPage(pageNumber: number) {
        if (this.paginatedService instanceof PaginatedEntityCollectionReadOnlyService) {
            this.paginatedService.additionalParameters = this.parameters;
            this.paginatedService.sort = this.sort;
            this.paginatedService.getPage(pageNumber);
        } else {
            this.paginatedService.findPaginated(Pageable.ofWithSort(pageNumber, this.pageSizeSafetly, this.sort), this.parameters);
        }
        this.pageChanged(pageNumber);
    }

    pageChanged(pageNumber: number) {
        if (this.cacheName) {
            this.paginationCacheService.put(this.cacheName, Pageable.ofWithSort(pageNumber, this.pageSizeSafetly, this.sort));
        }
    }

    print() {
        const css = `<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.0/css/bootstrap.min.css"
  integrity="sha384-9gVQ4dYFwwWSjIDZnLEWnxCjeSWFphJiwGPXr1jddIhOegiu1FwO5qRGvFXOdJZ4" crossorigin="anonymous">`;
        const printContents = document.getElementById('test_test').innerHTML;
        alert(printContents);
        const pageContent = `<!DOCTYPE html><html><head>${css}</head><body onload="window.print()">${printContents}</html>`;
        let popupWindow: Window;
        if (navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
            popupWindow = window.open(
                '',
                '_blank',
                'width=600,height=600,scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no'
            );
            popupWindow.window.focus();
            popupWindow.document.write(pageContent);
            popupWindow.document.close();
            popupWindow.onbeforeunload = event => {
                popupWindow.close();
            };
            popupWindow.onabort = event => {
                popupWindow.document.close();
                popupWindow.close();
            };
        } else {
            popupWindow = window.open('', '_blank', 'width=600,height=600');
            popupWindow.document.open();
            popupWindow.document.write(pageContent);
            popupWindow.document.close();
        }
    }

    addItemToPage(item: T) {
        this.page.content?.unshift(item);
    }

    removeItemFromPage(item: T) {
        const index = this.page.content?.indexOf(item);
        if (index > - 1) {
            this.page.content.splice(index, 1);
        }
    }
}
